/* eslint-disable no-undef */
import { useState, useEffect, useCallback, useRef } from 'react';
import './NeuroViewer.css';
import { usePatients } from './hooks/usePatients';
import { usePatientData } from './hooks/usePatientData';
import { useCenters } from './hooks/useCenters';
import { NormativeResults, usePatientZScores, DEFAULT_MONTAGE_TYPE, DEFAULT_ZSCORE_VISUALIZATION_SETTINGS } from './NormativeModeling';
import { loadPersistedState, persistState, clearPersistedState } from './utils/uiStatePersistence';
import { parseTextIntensityValues, applyAtlasTextIntensityToViewer } from './viewer/lib/atlasColoring';
import { DEFAULT_COLOR_MAP, ATLAS_LABEL_COLOR_MAP, DEFAULT_PANEL_VIEW, DEFAULT_SELECTED_FORMAT, DEFAULT_SELECTED_BACKGROUND, DEFAULT_BRAIN_OPACITY, DEFAULT_WIREFRAME_ENABLED, DEFAULT_SETTINGS_STATE, pickPersistedSettings } from './viewer/constants';
import { ViewerHeader } from './viewer/components/ViewerHeader';
import { FilterDropdown } from './viewer/components/FilterDropdown';
import { initBrainBrowser } from './viewer/lib/brainBrowserLoader';
import { ControlPanel } from './viewer/components/ControlPanel';
import { BrainVisualizer } from './viewer/components/BrainVisualizer';

export { FilterDropdown };

// -------------------------------
// Main Component
// -------------------------------
const NeuroViewer = () => {
    // Read the persisted UI state once on mount; each state below falls back to
    // its current default when nothing (or invalid data) is stored.
    const [persistedInitial] = useState(() => loadPersistedState());
    const [selectedCenters, setSelectedCenters] = useState(() => persistedInitial.selectedCenters ?? []);
    const [selectedPatients, setSelectedPatients] = useState(() => persistedInitial.selectedPatients ?? []);
    const [selectedFormat, setSelectedFormat] = useState(() => persistedInitial.selectedFormat ?? DEFAULT_SELECTED_FORMAT);
    const [selectedBackground, setSelectedBackground] = useState(() => persistedInitial.selectedBackground ?? DEFAULT_SELECTED_BACKGROUND);
    const [viewer, setViewer] = useState(null);
    const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
    const [isLargeScreen, setIsLargeScreen] = useState(true);
    const { centers, loading: centersLoading, error: centersError } = useCenters();
    const { patients, loading: patientsLoading, error: patientsError } = usePatients();
    const { fetchAndPlotPatient, fetchAndPlotPatientsBatch, clearPatientData, clearAllPatientData, updatePatientVisualization, error: patientError, loading: patientsDataLoading } = usePatientData(viewer);
    const { patientZScores, setPatientZScores, montageType, zScoreVisualizationSettings, setZScoreVisualizationSettings, setMontageType } = usePatientZScores();
    const [panelView, setPanelView] = useState(() => (
        // The normative modeling view is hidden for now, so only restore the
        // abnormalities or upload views; any other (stale or removed) persisted
        // value falls back to the default, otherwise the panel renders blank.
        ['abnormalities', 'upload'].includes(persistedInitial.panelView)
            ? persistedInitial.panelView
            : DEFAULT_PANEL_VIEW
    ));
    const [hasIntensityData, setHasIntensityData] = useState(false);
    const [selectedCoordinates, setSelectedCoordinates] = useState(null);
    const [selectedVertex, setSelectedVertex] = useState(null);
    const [selectedValue, setSelectedValue] = useState(null);
    const [selectedColor, setSelectedColor] = useState(null);
    const [brainOpacity, setBrainOpacity] = useState(() => persistedInitial.brainOpacity ?? DEFAULT_BRAIN_OPACITY);
    const [wireframeEnabled, setWireframeEnabled] = useState(() => persistedInitial.wireframeEnabled ?? DEFAULT_WIREFRAME_ENABLED);
    const [currentObjectFilename, setCurrentObjectFilename] = useState('');
    const [showPatientsLoading, setShowPatientsLoading] = useState(false);

    // Settings panel state. Non-serializable members (File objects, caches) always
    // start at their defaults; only the persisted serializable subset is restored.
    const [settingsState, setSettingsState] = useState(() => ({
        ...DEFAULT_SETTINGS_STATE,
        ...pickPersistedSettings(persistedInitial.settingsState)
    }));

    const brainVisualizerRef = useRef(null);
    const viewerRef = useRef(null);
    const hasAutoSelectedRef = useRef(false); // Track if we've auto-selected on initial load
    // Restore restored-patient plotting runs exactly once, after the viewer is ready.
    const hasRestoredPlotRef = useRef(false);
    const initialSelectedPatientsRef = useRef(persistedInitial.selectedPatients ?? []);
    const initialCameraViewRef = useRef(persistedInitial.cameraView ?? null);
    // Bumped by "Reset settings" to signal the viewer to drop applied intensity.
    const [resetNonce, setResetNonce] = useState(0);

    useEffect(() => {
        if (!patientsDataLoading) {
            setShowPatientsLoading(false);
            return undefined;
        }

        const timer = window.setTimeout(() => {
            setShowPatientsLoading(true);
        }, 250);

        return () => window.clearTimeout(timer);
    }, [patientsDataLoading]);

    // Set to true to automatically select and load all centers/patients when app loads
    const AUTO_LOAD_ALL_DATA = false;

    // Auto-select all centers and patients on initial load (if enabled)
    useEffect(() => {
        if (AUTO_LOAD_ALL_DATA && !hasAutoSelectedRef.current && centers.length > 0 && viewer) {
            hasAutoSelectedRef.current = true;

            // Select all centers
            const allCenterIds = centers.map(c => c.id);
            setSelectedCenters(allCenterIds);

            // Collect all patient IDs from all centers
            const allPatientIds = centers.reduce((acc, center) => {
                return [...acc, ...center.patients];
            }, []);

            // Remove duplicates
            const uniquePatientIds = [...new Set(allPatientIds)];
            setSelectedPatients(uniquePatientIds);

            // Batch fetch and plot all patients
            if (uniquePatientIds.length > 0) {
                fetchAndPlotPatientsBatch(uniquePatientIds, zScoreVisualizationSettings, patientZScores);
            }
        }
    }, [centers, viewer, fetchAndPlotPatientsBatch, zScoreVisualizationSettings, patientZScores, AUTO_LOAD_ALL_DATA]);

    const handleCenterToggle = useCallback((centerId) => {
        setSelectedCenters(prev => {
            const isSelected = prev.includes(centerId);
            const updatedCenters = isSelected
                ? prev.filter(id => id !== centerId)
                : [...prev, centerId];

            // Find the center object to get its patients
            const centerObj = centers.find(c => c.id === centerId);
            if (centerObj) {
                // If center is being selected, add all its patients that aren't already selected
                if (!isSelected) {
                    setSelectedPatients(prevPatients => {
                        const patientsToAdd = centerObj.patients.filter(
                            patientId => !prevPatients.includes(patientId)
                        );

                        return [...prevPatients, ...patientsToAdd];
                    });
                }
                // If center is being deselected, remove all its patients
                else {
                    setSelectedPatients(prevPatients => {
                        const remainingPatients = prevPatients.filter(
                            patientId => !centerObj.patients.includes(patientId)
                        );

                        // Clear data for removed patients
                        centerObj.patients.forEach(patientId => {
                            if (prevPatients.includes(patientId)) {
                                clearPatientData(patientId);
                            }
                        });

                        return remainingPatients;
                    });
                }
            }

            return updatedCenters;
        });
    }, [centers, clearPatientData]);

    const handlePatientToggle = useCallback((patientId) => {
        setSelectedPatients(prev => {
            const isSelected = prev.includes(patientId);
            if (isSelected) {
                // Remove patient data from view
                clearPatientData(patientId);
                return prev.filter(id => id !== patientId);
            } else {
                return [...prev, patientId];
            }
        });
    }, [clearPatientData]);

    // Viewer initialization and cleanup
    useEffect(() => {
        let mounted = true;

        const setupViewer = async () => {
            const newViewer = await initBrainBrowser();
            if (mounted && newViewer) {
                setViewer(newViewer);
                viewerRef.current = newViewer;
                // Global access for NormativePanel
                window.brainbrowserViewer = newViewer;
            }
        };

        setupViewer();

        return () => {
            mounted = false;
            if (viewerRef.current) {
                try {
                    if (typeof viewerRef.current.destroy === 'function') {
                        viewerRef.current.destroy();
                    } else if (typeof viewerRef.current.clearScreen === 'function') {
                        viewerRef.current.clearScreen();
                    }
                } catch (e) {
                    console.error("Error during viewer cleanup:", e);
                }
                viewerRef.current = null;
                window.brainbrowserViewer = null; // Clean up global reference
                setViewer(null);
            }
        };
    }, []);

    // Load default brain model when viewer is ready
    useEffect(() => {
        if (viewer && !currentObjectFilename) {
            const loadingElement = document.createElement('div');
            loadingElement.className = 'loading-overlay';
            const container = document.getElementById('brainbrowser');
            if (container) {
                container.appendChild(loadingElement);
            }

            // Use the selected hemisphere from settings state (defaults to 'lh_rh')
            const hemisphere = settingsState.selectedHemisphere || 'lh_rh';
            const modelFiles = {
                'lh': '/atlas/lh.pial.asc',
                'rh': '/atlas/rh.pial.asc',
                'lh_rh': '/atlas/lh_rh.pial.asc'
            };
            const modelFile = modelFiles[hemisphere] || '/atlas/lh_rh.pial.asc';

            // Load the brain model based on selected hemisphere
            viewer.loadModelFromURL(modelFile, {
                format: 'freesurferasc',
                complete: () => {
                    // Restore a persisted brain orientation/zoom from a previous
                    // session (applied before the fade-in so the brain appears in
                    // the saved position). Validated to avoid a bad off-screen view.
                    const savedView = initialCameraViewRef.current;
                    if (savedView && viewer.model) {
                        try {
                            const r = savedView.rotation;
                            const p = savedView.position;
                            if (r && [r.x, r.y, r.z].every(Number.isFinite)) {
                                viewer.model.rotation.set(r.x, r.y, r.z);
                            }
                            if (p && [p.x, p.y, p.z].every(Number.isFinite) && viewer.setCameraPosition) {
                                viewer.setCameraPosition(p.x, p.y, p.z);
                            }
                        } catch (error) {
                            console.warn('Could not restore brain view:', error);
                        }
                    }

                    // Set initial opacity to 0 for fade-in effect
                    viewer.model.traverse(object => {
                        if (object.type === 'Mesh' &&
                            object.geometry &&
                            object.geometry.type !== 'SphereGeometry' &&
                            !object.name?.includes('-points') &&
                            object.name !== 'uploaded-points') {

                            if (object.material) {
                                object.material.transparent = true;
                                object.material.opacity = 0;

                                if (Array.isArray(object.material)) {
                                    object.material.forEach(material => {
                                        material.transparent = true;
                                        material.opacity = 0;
                                    });
                                }
                            }
                        }
                    });

                    viewer.render();
                    if (loadingElement.parentNode) {
                        loadingElement.remove();
                    }

                    // Fade in the model over 1 second
                    const fadeDuration = 1000; // milliseconds
                    const startTime = Date.now();
                    const startOpacity = 0;
                    // Fade to the restored/current opacity (not always full), so a
                    // persisted opacity takes visual effect on refresh.
                    const targetOpacity = Math.max(0, Math.min(typeof brainOpacity === 'number' ? brainOpacity : 1, 1));

                    const fadeIn = () => {
                        const elapsed = Date.now() - startTime;
                        const progress = Math.min(elapsed / fadeDuration, 1);
                        // Use ease-out easing for smooth fade
                        const easedProgress = 1 - Math.pow(1 - progress, 3);
                        const currentOpacity = startOpacity + (targetOpacity - startOpacity) * easedProgress;

                        viewer.model.traverse(object => {
                            if (object.type === 'Mesh' &&
                                object.geometry &&
                                object.geometry.type !== 'SphereGeometry' &&
                                !object.name?.includes('-points') &&
                                object.name !== 'uploaded-points') {

                                if (object.material) {
                                    object.material.opacity = currentOpacity;

                                    if (Array.isArray(object.material)) {
                                        object.material.forEach(material => {
                                            material.opacity = currentOpacity;
                                        });
                                    }
                                }
                            }
                        });

                        viewer.render();

                        if (progress < 1) {
                            requestAnimationFrame(fadeIn);
                        } else {
                            // Animation complete - settle on the target opacity
                            const opaque = targetOpacity >= 1;
                            viewer.model.traverse(object => {
                                if (object.type === 'Mesh' &&
                                    object.geometry &&
                                    object.geometry.type !== 'SphereGeometry' &&
                                    !object.name?.includes('-points') &&
                                    object.name !== 'uploaded-points') {

                                    if (object.material) {
                                        object.material.opacity = targetOpacity;
                                        // Only disable transparency when fully opaque (perf).
                                        object.material.transparent = !opaque;

                                        if (Array.isArray(object.material)) {
                                            object.material.forEach(material => {
                                                material.opacity = targetOpacity;
                                                material.transparent = !opaque;
                                            });
                                        }
                                    }
                                }
                            });
                            viewer.render();
                        }
                    };

                    // Start the fade-in animation
                    requestAnimationFrame(fadeIn);

                    // Update filename based on hemisphere
                    const filename = hemisphere === 'lh_rh' ? 'lh_rh.pial.asc' : `${hemisphere}.pial.asc`;
                    setCurrentObjectFilename(filename);
                    setSettingsState(prev => ({ ...prev, objectApplied: true }));

                    // Load atlas intensity file if enabled
                    if (settingsState.giiEnabled) {
                        const atlasIntensityFiles = {
                            'lh': { url: '/atlas/atlas_lh.txt', format: 'text' },
                            'rh': { url: '/atlas/atlas_rh.txt', format: 'text' },
                            'lh_rh': { url: '/atlas/atlas.txt', format: 'text' }
                        };
                        const intensityFile = atlasIntensityFiles[hemisphere];
                        if (intensityFile) {
                            const colorMapToUse = intensityFile.format === 'text' &&
                                (!settingsState.selectedColorMap || settingsState.selectedColorMap === DEFAULT_COLOR_MAP)
                                ? ATLAS_LABEL_COLOR_MAP
                                : settingsState.selectedColorMap || DEFAULT_COLOR_MAP;

                            if (intensityFile.format === 'text') {
                                Promise.all([
                                    fetch(intensityFile.url).then(response => {
                                        if (!response.ok) throw new Error(`Could not load atlas file (${response.status}).`);
                                        return response.text();
                                    }),
                                    fetch(colorMapToUse).then(response => {
                                        if (!response.ok) throw new Error(`Could not load atlas color map (${response.status}).`);
                                        return response.text();
                                    })
                                ]).then(([intensityText, colorMapText]) => {
                                    applyAtlasTextIntensityToViewer({
                                        viewer,
                                        values: parseTextIntensityValues(intensityText),
                                        colorMapText
                                    });
                                    setSettingsState(prev => ({
                                        ...prev,
                                        intensityApplied: true,
                                        selectedColorMap: colorMapToUse,
                                        intensityFormatWarning: null
                                    }));
                                    setHasIntensityData(true);
                                }).catch((error) => {
                                    console.error('Error applying default atlas text intensity file:', error);
                                    setSettingsState(prev => ({
                                        ...prev,
                                        intensityApplied: false,
                                        intensityFormatWarning: error?.message || 'Could not apply atlas text colors.'
                                    }));
                                });
                                return;
                            }

                            viewer.loadColorMapFromURL(colorMapToUse, {
                                cursor_color: "#FFFFFF",
                                complete: () => {
                                    viewer.loadIntensityDataFromURL(intensityFile.url, {
                                        format: intensityFile.format,
                                        blend: false,
                                        complete: () => {
                                            setSettingsState(prev => ({
                                                ...prev,
                                                intensityApplied: true,
                                                selectedColorMap: colorMapToUse
                                            }));
                                            setHasIntensityData(true);
                                            viewer.render();
                                        },
                                        error: (error) => {
                                            console.error('Error loading default atlas intensity file:', error);
                                        }
                                    });
                                },
                                error: (error) => {
                                    console.error('Error loading default atlas color map:', error);
                                }
                            });
                        }
                    }
                },
                error: (error) => {
                    console.error('Error loading default brain model:', error);
                    if (loadingElement.parentNode) {
                        loadingElement.remove();
                    }
                }
            });
        }
    }, [viewer, currentObjectFilename, setCurrentObjectFilename, setSettingsState, settingsState.selectedHemisphere, settingsState.giiEnabled, settingsState.selectedColorMap, setHasIntensityData, brainOpacity]);


    // If the viewer reloads, redraw only already-generated z-score overlays.
    useEffect(() => {
        if (
            viewer &&
            zScoreVisualizationSettings.enabled &&
            selectedPatients.length > 0 &&
            patientZScores.length > 0 &&
            centers.length === 0
        ) {
            updatePatientVisualization(selectedPatients, zScoreVisualizationSettings, patientZScores);
        }
    }, [viewer, selectedPatients, centers.length, updatePatientVisualization, zScoreVisualizationSettings, patientZScores]);

    // Restore saved selections when centers data is loaded and viewer is ready
    useEffect(() => {
        if (viewer && centers.length > 0 && hasAutoSelectedRef.current) {
            if (selectedCenters.length > 0) {
                // Collect all patients from selected centers
                const patientsToLoad = [];
                centers.forEach(center => {
                    if (selectedCenters.includes(center.id)) {
                        // Find patients from this center that should be loaded
                        const centerPatients = center.patients.filter(
                            patientId => selectedPatients.includes(patientId)
                        );
                        patientsToLoad.push(...centerPatients);
                    }
                });

                if (
                    patientsToLoad.length > 0 &&
                    zScoreVisualizationSettings.enabled &&
                    patientZScores.length > 0
                ) {
                    updatePatientVisualization(patientsToLoad, zScoreVisualizationSettings, patientZScores);
                }
            } else if (selectedPatients.length > 0) {
                if (zScoreVisualizationSettings.enabled && patientZScores.length > 0) {
                    updatePatientVisualization(selectedPatients, zScoreVisualizationSettings, patientZScores);
                }
            }
        }
    }, [viewer, centers, selectedCenters, selectedPatients, updatePatientVisualization, zScoreVisualizationSettings, patientZScores]);

    // Persist the serializable slice of UI state whenever any of it changes so it
    // survives a browser refresh. Only the four serializable settingsState keys
    // are written (File objects / caches are intentionally excluded).
    useEffect(() => {
        persistState({
            selectedCenters,
            selectedPatients,
            panelView,
            selectedFormat,
            selectedBackground,
            brainOpacity,
            wireframeEnabled,
            settingsState: pickPersistedSettings(settingsState)
        });
    }, [
        selectedCenters,
        selectedPatients,
        panelView,
        selectedFormat,
        selectedBackground,
        brainOpacity,
        wireframeEnabled,
        settingsState
    ]);

    // Persist the brain orientation/zoom (model rotation + camera position) once
    // the user finishes an interaction, so the view survives a refresh. Debounced
    // and merged into the same persisted UI-state key.
    useEffect(() => {
        if (!viewer) {
            return undefined;
        }
        const container = document.getElementById('brainbrowser');
        if (!container) {
            return undefined;
        }
        let timer = null;
        const captureView = () => {
            window.clearTimeout(timer);
            timer = window.setTimeout(() => {
                try {
                    const model = viewer.model;
                    const cam = viewer.getCameraPosition?.();
                    if (!model || !cam) {
                        return;
                    }
                    const view = {
                        rotation: { x: model.rotation.x, y: model.rotation.y, z: model.rotation.z },
                        position: { x: cam.x, y: cam.y, z: cam.z }
                    };
                    const nums = [
                        view.rotation.x, view.rotation.y, view.rotation.z,
                        view.position.x, view.position.y, view.position.z
                    ];
                    if (nums.every(Number.isFinite)) {
                        persistState({ cameraView: view });
                    }
                } catch (error) {
                    // Non-fatal: skip persisting this view.
                }
            }, 400);
        };
        document.addEventListener('mouseup', captureView);
        container.addEventListener('wheel', captureView, { passive: true });
        container.addEventListener('touchend', captureView);
        return () => {
            window.clearTimeout(timer);
            document.removeEventListener('mouseup', captureView);
            container.removeEventListener('wheel', captureView);
            container.removeEventListener('touchend', captureView);
        };
    }, [viewer]);

    // Apply the (possibly restored) background color to the viewer once it is
    // ready and whenever it changes, so a persisted background takes visual effect.
    useEffect(() => {
        if (viewer && selectedBackground) {
            const parsed = parseInt(selectedBackground, 16);
            if (!Number.isNaN(parsed)) {
                viewer.setClearColor(parsed);
            }
        }
    }, [viewer, selectedBackground]);

    // Re-plot patients that were restored from a previous session. Selecting a
    // patient does not plot directly (the AbnormalitiesPanel's debounced auto-fire
    // does), and that panel only runs while its tab is active. To avoid a
    // "selected but not plotted" mismatch (e.g. when a non-abnormalities tab was
    // restored), plot the restored patients once the viewer is ready using the
    // point data already cached by usePatientData. Runs exactly once.
    useEffect(() => {
        if (hasRestoredPlotRef.current || !viewer) {
            return;
        }
        hasRestoredPlotRef.current = true;
        // The AbnormalitiesPanel owns an authoritative selection->plot effect that
        // replots the restored patients WITH z-score coloring whenever that tab is
        // active. Plotting here too would race it and could overwrite those colors
        // with plain points, so only cover the non-abnormalities case (restored
        // patients still appear on the brain regardless of the active tab).
        if (panelView === 'abnormalities') {
            return;
        }
        const restoredPatients = initialSelectedPatientsRef.current;
        if (restoredPatients.length > 0) {
            fetchAndPlotPatientsBatch(restoredPatients);
        }
    }, [viewer, fetchAndPlotPatientsBatch, panelView]);

    // Reset control: deselect every patient/center, remove their plotted points,
    // and clear the persisted selection (point-data caches are left intact).
    const handleDeselectAllPatients = useCallback(() => {
        setSelectedPatients([]);
        setSelectedCenters([]);
        clearAllPatientData?.();
        persistState({ selectedPatients: [], selectedCenters: [] });
    }, [clearAllPatientData]);

    // A federated upload finished computing z-scores in the browser. Merge the
    // local patients into the shared z-score state, select them, and plot right
    // away. The plotting path already treats local_federated patients specially
    // (their coordinates ride along in patientZScores, so no backend fetch).
    const handleLocalPatientsLoaded = useCallback(({ patientIds, zScoreData, feature }) => {
        if (!patientIds?.length || !zScoreData?.length) {
            return;
        }
        const localIds = new Set(patientIds);
        const mergedZScores = [
            ...patientZScores.filter(patient => !localIds.has(patient.patient_id)),
            ...zScoreData
        ];
        const mergedSelection = [...new Set([...selectedPatients, ...patientIds])];
        const nextSettings = {
            ...zScoreVisualizationSettings,
            enabled: true,
            feature: zScoreVisualizationSettings.feature || feature
        };

        setPatientZScores(mergedZScores);
        setZScoreVisualizationSettings(nextSettings);
        setSelectedPatients(mergedSelection);
        updatePatientVisualization(mergedSelection, nextSettings, mergedZScores);
    }, [patientZScores, selectedPatients, zScoreVisualizationSettings, setPatientZScores, setZScoreVisualizationSettings, updatePatientVisualization]);

    // Reset control: restore all settings/display state to their original
    // defaults and clear the persisted UI-state key (patient-data caches kept).
    const handleResetSettings = useCallback(() => {
        setSettingsState({ ...DEFAULT_SETTINGS_STATE });
        setZScoreVisualizationSettings({ ...DEFAULT_ZSCORE_VISUALIZATION_SETTINGS });
        setMontageType(DEFAULT_MONTAGE_TYPE);
        setSelectedFormat(DEFAULT_SELECTED_FORMAT);
        setSelectedBackground(DEFAULT_SELECTED_BACKGROUND);
        setBrainOpacity(DEFAULT_BRAIN_OPACITY);
        setWireframeEnabled(DEFAULT_WIREFRAME_ENABLED);
        setPanelView(DEFAULT_PANEL_VIEW);
        // Reset the brain orientation/zoom immediately too (clearPersistedState
        // also drops the persisted cameraView so it won't return on refresh).
        viewer?.resetView?.();
        // Signal the viewer to reload the default surface without intensity, so the
        // applied colormap/atlas coloring is actually removed (resetting giiEnabled
        // in state alone does not repaint the already-colored mesh).
        setResetNonce(n => n + 1);
        clearPersistedState();
    }, [viewer, setZScoreVisualizationSettings, setMontageType]);

    // Add a useEffect to check screen width
    useEffect(() => {
        const checkScreenSize = () => {
            setIsLargeScreen(window.innerWidth > 1200);
        };

        // Initial check
        checkScreenSize();

        // Listen for resize events
        window.addEventListener('resize', checkScreenSize);

        // Cleanup
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (brainVisualizerRef.current && typeof brainVisualizerRef.current.triggerViewportUpdate === 'function') {
                brainVisualizerRef.current.triggerViewportUpdate();
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [isPanelCollapsed, isLargeScreen]);

    return (
        <div className="neuro-viewer">
            {showPatientsLoading && (
                <div className="global-interactions-shield" aria-hidden="true"></div>
            )}
            <ViewerHeader
                selectedCenters={selectedCenters}
                selectedPatients={selectedPatients}
                onCenterToggle={handleCenterToggle}
                onPatientToggle={handlePatientToggle}
                patientError={patientError}
            />
            <div className={`viewer-content ${isLargeScreen && isPanelCollapsed ? 'collapsed' : ''}`}>
                <div className="viewer-main">
                    <BrainVisualizer
                        ref={brainVisualizerRef}
                        viewer={viewer}
                        hasIntensityData={hasIntensityData}
                        setHasIntensityData={setHasIntensityData}
                        selectedCoordinates={selectedCoordinates}
                        setSelectedCoordinates={setSelectedCoordinates}
                        selectedVertex={selectedVertex}
                        setSelectedVertex={setSelectedVertex}
                        selectedValue={selectedValue}
                        setSelectedValue={setSelectedValue}
                        selectedColor={selectedColor}
                        setSelectedColor={setSelectedColor}
                        brainOpacity={brainOpacity}
                        setBrainOpacity={setBrainOpacity}
                        wireframeEnabled={wireframeEnabled}
                        setWireframeEnabled={setWireframeEnabled}
                        currentObjectFilename={currentObjectFilename}
                        selectedFormat={selectedFormat}
                        setSelectedFormat={setSelectedFormat}
                        selectedBackground={selectedBackground}
                        setSelectedBackground={setSelectedBackground}
                        setCurrentObjectFilename={setCurrentObjectFilename}
                        settingsState={settingsState}
                        setSettingsState={setSettingsState}
                        patientsDataLoading={showPatientsLoading}
                        onResetSettings={handleResetSettings}
                        resetNonce={resetNonce}
                    />
                </div>
                <div className="viewer-sidebar">
                    <ControlPanel
                        viewer={viewer}
                        selectedFormat={selectedFormat}
                        setSelectedFormat={setSelectedFormat}
                        selectedBackground={selectedBackground}
                        setSelectedBackground={setSelectedBackground}
                        selectedCenters={selectedCenters}
                        selectedPatients={selectedPatients}
                        onCenterToggle={handleCenterToggle}
                        onPatientToggle={handlePatientToggle}
                        patientError={patientError}
                        centers={centers}
                        patients={patients}
                        centersLoading={centersLoading}
                        centersError={centersError}
                        patientsLoading={patientsLoading}
                        patientsError={patientsError}
                        isPanelCollapsed={isPanelCollapsed}
                        setIsPanelCollapsed={setIsPanelCollapsed}
                        isLargeScreen={isLargeScreen}
                        panelView={panelView}
                        setPanelView={setPanelView}
                        hasIntensityData={hasIntensityData}
                        setHasIntensityData={setHasIntensityData}
                        setSelectedCoordinates={setSelectedCoordinates}
                        setSelectedVertex={setSelectedVertex}
                        setSelectedValue={setSelectedValue}
                        setSelectedColor={setSelectedColor}
                        setBrainOpacity={setBrainOpacity}
                        brainOpacity={brainOpacity}
                        wireframeEnabled={wireframeEnabled}
                        setWireframeEnabled={setWireframeEnabled}
                        setCurrentObjectFilename={setCurrentObjectFilename}
                        settingsState={settingsState}
                        setSettingsState={setSettingsState}
                        applyOpacityToScene={brainVisualizerRef.current?.applyOpacityToScene}
                        applyWireframeState={brainVisualizerRef.current?.applyWireframeState}
                        brainCaptureApi={brainVisualizerRef.current}
                        patientDataApi={{
                            fetchAndPlotPatient,
                            fetchAndPlotPatientsBatch,
                            clearPatientData,
                            clearAllPatientData,
                            updatePatientVisualization,
                            patientDataError: patientError,
                            viewerReady: Boolean(viewer)
                        }}
                        onDeselectAllPatients={handleDeselectAllPatients}
                        onLocalPatientsLoaded={handleLocalPatientsLoaded}
                        montageType={montageType}
                        loading={patientsDataLoading}
                    />
                </div>
            </div>

            <div className="normative-results-wrapper" id="normative-results">
                <NormativeResults />
            </div>
        </div>
    );
};

export default NeuroViewer;









